import type { AxiosError } from "axios";
import parsePhoneNumberFromString from "libphonenumber-js";
import { useState } from "react";
import { PhoneInput } from "react-international-phone";
import { Spinner } from "@/components/Spinner";
import { UseData } from "@/components/user/lib/services/UseData";
import { cn } from "@/lib/utils";
import {
	formClassNames,
	formHeaderClassNames,
} from "@/shared/constants/customStyleSets";

const isPhoneValid = (phone: string) => {
	try {
		const phoneNumber = parsePhoneNumberFromString(phone);
		return phoneNumber?.isValid() ?? false;
	} catch {
		return false;
	}
};

type Props = {
	setFormName: (formName: "phone" | "confirm") => void;
	setPhone: (phone: string) => void;
	phone: string;
	showTitle?: boolean;
	className?: string;
};

export const EnterPhone: React.FC<Props> = ({
	setFormName,
	setPhone,
	phone,
	showTitle = true,
	className = "",
}) => {
	const [validationError, setValidationError] = useState("");
	const isValid = isPhoneValid(phone);

	const { error, isLoading, execute } = UseData.create().useSavePhone();

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setValidationError("");

		if (!isValid) {
			setValidationError("Invalid phone number");
			return;
		}

		await execute({ phone });

		setFormName("confirm");
		setValidationError("");
	};

	return (
		<form
			onSubmit={handleSubmit}
			className={cn(formClassNames, "sm:w-full md:p-0", className)}
			data-testid="enter-phone-form"
		>
			{showTitle && (
				<div className={formHeaderClassNames} data-testid="phone-form-title">
					<p>Thank you for becoming an </p>
					<p>EPSM Premium Client!</p>
				</div>
			)}

			<p className="mb-4 text-center">
				We value your personal security and want to ensure we can provide you
				with an elevated customer service experience.
			</p>

			<p className="text-center">
				To authenticate your account, please enter your cell phone number and
				click on the SEND button to receive your security code
			</p>

			<div className="max-w-md px-4 mt-4">
				<label htmlFor="phone-input" className="sr-only">
					Phone number
				</label>

				<PhoneInput
					defaultCountry="us"
					value={phone}
					onChange={(phone) => setPhone(phone)}
					className="PhoneInput"
					data-testid="phone-input"
				/>
			</div>

			<div className="flex align-center justify-center mt-6 mb-4">
				<button
					disabled={isLoading}
					type="submit"
					className="submit-btn px-6 py-2 bg-gold hover:bg-gray text-black hover:text-white font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 cursor-pointer"
				>
					Send
				</button>
			</div>

			{isLoading && <Spinner />}

			<p className="text-red-500">
				{validationError && validationError}

				{(error as AxiosError)?.status === 400 &&
					"Ooops! Something went wrong. Try again later."}
			</p>
		</form>
	);
};
